import { z } from 'zod';
import type { ProvaRuoliLinkDto, SourceGrade } from './contratto';
import type { ClosingCommonDto, ScrapCertainty } from './fase10';

/**
 * La fase 11 «Le proposte, i benefici e il percorso di realizzazione» del contratto n. 1 versione 2.2 (art. 8, fase 11;
 * art. 3.42-3.49; ADR 0179), nella forma della pagina di prova `docs/mockup/percorso-sinapsi/14-fase11`; poi ciò che il
 * cliente riceve alla consegna (art. 4.2 e 4.4, art. 12): il documento scaricabile e l'offerta del contratto n. 2.
 */

/** La quota prudente (art. 3.46): la parte massima del tempo di oggi che una proposta può dire di liberare. */
export const QUOTA_PRUDENTE = 0.3;

/** Chi fa una proposta (art. 3.42, art. 7.12): una persona, una persona con un agente, un agente. */
export const PROPOSAL_WHO = ['persona', 'persona_con_agente', 'agente'] as const;
export type ProposalWho = (typeof PROPOSAL_WHO)[number];
export const PROPOSAL_WHO_TEXT: Record<ProposalWho, string> = { persona: 'Persona', persona_con_agente: 'Persona con agente', agente: 'Agente' };

/** Le ipotesi di un beneficio (art. 3.39): frequenza (volte al mese), durata (minuti per volta), persone, volume. */
export const HYPOTHESIS_NAMES = ['frequenza', 'durata', 'persone', 'volume'] as const;
export type HypothesisName = (typeof HYPOTHESIS_NAMES)[number];

/** Da dove viene un'ipotesi, come la pagina lo dichiara sotto il calcolo (art. 8, fase 11, chiede al titolare). */
export type HypothesisOrigin = 'titolare' | 'scelta_timonio' | 'stima_settore' | 'fase';

/** Un intervallo prudente (art. 3.45): da-a; `a` null = «oltre `da`». */
export interface RangeDto {
  da: number;
  a: number | null;
}

export interface HypothesisDto {
  nome: HypothesisName;
  /** Ciò che si conta, in parole («rapporti al mese», «minuti per ricopiare un rapporto»). */
  cosa: string;
  valore: RangeDto;
  origine: HypothesisOrigin;
  /** Il perché del consulente o la fase da cui viene. */
  perche: string;
}

/** Il calcolo visibile sotto il numero (art. 3.44): ore = frequenza × durata × persone; euro = ore × costo orario. */
export interface ClosingBenefitDto {
  ore_oggi: RangeDto;
  /** La parte liberata, in percentuale, sempre entro la quota prudente. */
  quota: { da: number; a: number };
  ore: RangeDto;
  costo_orario: { valore: number; formula: string; fonte: string; breve: string } | null;
  euro: RangeDto | null;
  /** Le righe del calcolo come la pagina le mostra, nell'ordine. */
  righe: string[];
  /** La riga delle ipotesi, sotto il calcolo. */
  ipotesi: string;
  /** La quota del consulente era oltre la quota prudente: lo studio l'ha riportata al massimo. */
  riportata: boolean;
}

export interface SanctionDto {
  testo: string;
  /** L'importo minimo che la norma prevede, in euro: è l'unico che entra nei totali (art. 3.43). */
  minimo: number | null;
  /** L'importo massimo, in euro: si mostra solo accanto all'obbligo, mai in un totale (art. 3.43). */
  massimo: number | null;
  norma: string;
}

export interface ClosingProposalDto {
  /** Il numero nel percorso, in ordine di priorità («01»). */
  n: number;
  scarto: { n: number; id: string; testo: string; certezza: ScrapCertainty; fonte: string | null };
  titolo: string;
  cosa_cambia: string;
  chi: ProposalWho;
  programma: string | null;
  beneficio: ClosingBenefitDto | null;
  sanzioni: SanctionDto[];
  intangibili: Array<{ testo: string; perche: string; fonte: string }>;
  percorso: { partenza: string; traguardo: string; cosa_serve: string; misura: string };
  reparto: string | null;
  ruolo: string | null;
  /** Il secondo ruolo che chi fa il primo copre (art. 8, fase 11, consegna). */
  copre: string | null;
  /** Si mostra aperta in cima (obblighi scoperti e benefici con il calcolo); le altre stanno nella tabella e si aprono. */
  aperta: boolean;
  /** In testa al percorso: un obbligo scoperto (art. 3.48). */
  obbligo: boolean;
}

export interface FreedHoursDto {
  reparto: string;
  ruolo: string;
  ore: RangeDto;
  /** Le proposte da cui vengono («08, 10»). */
  da: number[];
}

/* ------------------------------------------------------------------ la mappa «Timonio nell'azienda» (art. 3.47) */

/**
 * I gradi di automazione di un compito (art. 3.47), definiti per regola: A lo fa l'agente; B l'agente prepara e una
 * persona conferma; C la persona lo fa con Timonio; D resta alla persona.
 */
export const AUTOMATION_GRADES = ['A', 'B', 'C', 'D'] as const;
export type AutomationGrade = (typeof AUTOMATION_GRADES)[number];
export const AUTOMATION_GRADE_TEXT: Record<AutomationGrade, string> = {
  A: 'Lo fa l’agente',
  B: 'L’agente prepara, la persona conferma',
  C: 'La persona lo fa con Timonio',
  D: 'Resta alla persona',
};

/**
 * La quota del grado C (fase 11, consegna): la parte del tempo di un compito che Timonio libera quando la persona lo fa
 * con Timonio, cioè solo il tempo per cercare, ricordare e registrare; più bassa della quota prudente (art. 3.46).
 * Valore di partenza, da confermare alla prima misura.
 */
export const QUOTA_GRADO_C = 0.1;

/** Un compito della mappa: dalle mansioni della fase 8 o dai passaggi delle procedure della fase 9, con grado e valore. */
export interface TaskValueDto {
  /** `fase8:<ruolo>:<mansione>:<compito>` o `fase9:<procedura>:<passaggio>` (allegato, C.2). */
  rif: string;
  da: 'mansione' | 'procedura';
  testo: string;
  /** La mansione o la procedura a cui appartiene. */
  dove: string;
  grado: AutomationGrade;
  perche: string;
  /** Ore ed euro con il calcolo (A e B: quota prudente; C: quota del grado C; D: nessun valore). */
  beneficio: ClosingBenefitDto | null;
  /** Un compito di una mansione che è lo stesso lavoro di un passaggio: si conta lì («procedura «…», passaggio 3»). */
  contato_in: string | null;
}

/** Una somma della mappa: ore ed euro al mese («almeno» = estremo basso), con quanti compiti la fanno. */
export interface TaskSumDto {
  ore: RangeDto;
  /** Solo dai compiti con un costo orario; quelli senza contano nelle ore. */
  euro: RangeDto | null;
  compiti: number;
  senza_euro: number;
}

/** I compiti di un ruolo, per grado, e la somma che conta (art. 3.47). */
export interface TaskRoleDto {
  ruolo: string;
  compiti: TaskValueDto[];
  gradi: Record<AutomationGrade, number>;
  ab: TaskSumDto | null;
  c: TaskSumDto | null;
  /**
   * Nella mappa del primo passo, quale vista del ruolo entra nei totali: le mansioni (fase 8) o le procedure (fase 9), la
   * più alta. Con le somme per procedura (mappa collegata) ogni compito si conta una volta sola e qui resta null.
   */
  conta: 'mansioni' | 'procedure' | null;
}

/** Una procedura della mappa: i suoi passaggi con grado e valore, la somma e la parte che copre Timonio. */
export interface TaskProcedureDto {
  nome: string;
  /** Gli altri processi a cui appartiene: si conta nel primo e negli altri si nomina. */
  anche_in: string[];
  compiti: TaskValueDto[];
  ab: TaskSumDto | null;
  c: TaskSumDto | null;
  /** Quanti passaggi hanno il grado A, B o C, su tutti i passaggi. */
  copre: { abc: number; totale: number };
}

/**
 * Un processo della mappa con le sue procedure, per reparto e sede; più due gruppi: le procedure degli obblighi (fase 6) e
 * i compiti fuori dalle procedure, ruolo per ruolo (ogni ruolo come una «procedura» del gruppo).
 */
export interface TaskProcessDto {
  tipo: 'processo' | 'obblighi' | 'fuori';
  nome: string;
  reparto: string | null;
  sede: string | null;
  procedure: TaskProcedureDto[];
  ab: TaskSumDto | null;
  c: TaskSumDto | null;
}

/** La mappa «Timonio nell'azienda» (art. 3.47; fase 11, consegna), primo passo: i compiti per ruolo e grado. */
export interface TaskMapDto {
  ruoli: TaskRoleDto[];
  gradi: Record<AutomationGrade, number>;
  /** Il totale di A e B: la base del prezzo del contratto n. 2 (art. 4.4). */
  ab: TaskSumDto | null;
  /** Il totale di C: si mostra a parte e non entra nel prezzo. */
  c: TaskSumDto | null;
  classificati: number;
  totali: number;
  /** Le somme per procedura e per processo, con ogni compito contato una volta (secondo passo); false nella mappa di prima. */
  collegata: boolean;
  processi: TaskProcessDto[];
  /** Come si sommano le viste, e cosa manca (blocchi non consegnati, gradi tolti dal verificatore). */
  note: string[];
}

export interface PhaseElevenDto extends ClosingCommonDto {
  fase: 11;
  numeri: { proposte: number; ore: RangeDto | null; euro: RangeDto | null; /** La somma degli importi minimi, uno per obbligo, e da quante proposte viene (art. 3.43). */
    sanzioni: { minimo: number; obblighi: number } | null };
  proposte: ClosingProposalDto[];
  ore_per_ruolo: FreedHoursDto[];
  totale_ore: RangeDto | null;
  /** L'incarico chiuso dal titolare con la conferma della fase 11 (art. 12.2). */
  chiuso_il: string | null;
  /** La mappa «Timonio nell'azienda» (art. 3.47); null finché il consulente non ha classificato i compiti. */
  timonio: TaskMapDto | null;
}

/* ------------------------------------------------------------------ la consegna (art. 4, art. 12) */

/** Un pezzo di una sezione del documento: un testo, una tabella, un elenco di voci con la fonte. */
export type DocumentBlockDto =
  | { tipo: 'testo'; testo: string }
  | { tipo: 'tabella'; intestazione: string[]; righe: string[][] }
  | { tipo: 'voci'; voci: Array<{ nome: string; testo: string | null; fonte: string | null; grado: string | null }> };

/**
 * Le sezioni del documento scaricabile, nell'ordine delle fasi della pagina (art. 4.2, versione 2.7): la nota di tutela
 * non è una sezione, sta nella prima pagina (contratto 0, art. 8.4). Le proposte sono in sintesi; il dettaglio sta
 * nell'allegato in fondo.
 */
export const DOCUMENT_SECTIONS = [
  ['atto', 'L’atto dell’impresa'],
  ['conti', 'I conti'],
  ['conti_letti', 'I conti letti'],
  ['profilo', 'Il profilo'],
  ['sedi', 'Le sedi, con gli attributi'],
  ['attivita', 'Le attività'],
  ['processi', 'I processi'],
  ['impianti', 'Gli impianti'],
  ['obblighi', 'Il registro degli obblighi, con la norma'],
  ['programmi', 'I programmi e le piattaforme'],
  ['organigramma', 'Organigramma e funzionigramma'],
  ['ruoli', 'Le schede dei ruoli'],
  ['dimensionamento', 'Il dimensionamento, con lo scarto'],
  ['procedure', 'Le procedure'],
  ['diagnosi', 'La diagnosi'],
  ['proposte', 'Le proposte, in sintesi'],
  ['fonti', 'Le fonti, con il grado e la data'],
  ['titolare', 'Cosa ci ha detto il titolare'],
  ['scelte', 'Le scelte di Timonio e le domande ancora aperte'],
  // L'allegato «Il dettaglio delle proposte», in fondo (fase 11, consegna).
  ['dettaglio', 'Allegato · Le proposte con il calcolo'],
  ['percorso', 'Allegato · Il percorso di realizzazione'],
  ['timonio', 'Allegato · Timonio nell’azienda, per processo e per ruolo'],
] as const;
export type DocumentSectionKey = (typeof DOCUMENT_SECTIONS)[number][0];

export interface DocumentSectionDto {
  chiave: DocumentSectionKey;
  titolo: string;
  /** La fase da cui viene, null per le sezioni dell'incarico (fonti, titolare, scelte). */
  fase: number | null;
  /** Il livello della fase, quando c'è (art. 4.1). */
  livello: number | null;
  /** La fase non è ancora pronta: la sezione lo dice, non inventa. */
  pronta: boolean;
  blocchi: DocumentBlockDto[];
}

/** Il documento scaricabile (art. 4.2; art. 3.17): le stesse voci della pagina, nello stesso ordine. */
export interface ProjectDocumentDto {
  azienda: string;
  /** La nota di tutela del contratto 0 (art. 8.4), nella prima pagina. */
  nota: string;
  partita_iva: string | null;
  il: string;
  sezioni: DocumentSectionDto[];
}

/** Una fonte dell'elenco del documento, con grado e data (art. 4.2). */
export interface DocumentSourceDto {
  nome: string;
  grado: SourceGrade | null;
  data: string | null;
}

/** Il lavoro di Timonio per un ruolo nell'offerta del contratto n. 2: i compiti di grado A e B della mappa (art. 4.4). */
export interface OfferRoleDto {
  ruolo: string;
  lavori: Array<{ testo: string; grado: 'A' | 'B' }>;
  ore: RangeDto | null;
  euro: RangeDto | null;
}

/** L'offerta del contratto n. 2, composta dallo studio dalle voci del progetto, senza nuove ricerche (art. 4.4). */
export interface ContractTwoOfferDto {
  pronta: boolean;
  /** Perché non è ancora pronta (la fase 11 non c'è) o perché manca il prezzo. */
  perche: string | null;
  ruoli: OfferRoleDto[];
  /** Il calcolo del ritorno davanti al titolare: valore al mese, prezzo, giorni per ripagarlo. */
  ritorno: {
    valore_mese: RangeDto | null;
    prezzo_mese: number | null;
    quota: number;
    prova_giorni: number;
    giorni_per_ripagare: number | null;
    formula: string;
  };
  /** Il grado C (la persona lo fa con Timonio): si mostra come beneficio, ma non entra nel prezzo (art. 4.4). */
  grado_c: TaskSumDto | null;
  /** Le sanzioni evitate: si mostrano, ma non entrano nel prezzo (art. 4.4). */
  sanzioni: Array<{ proposta: number; testo: string; norma: string }>;
  /** Il link di prova dei ruoli (contratto 2.2, art. 4.4 e 12.1; ADR 0173); null finché il titolare non lo crea. */
  prova: ProvaRuoliLinkDto | null;
  /** L'offerta non impegna (art. 4.4). */
  nota: string;
}

/** La chiusura dell'incarico (art. 12): cosa manca per dirlo eseguito e cosa il titolare ha deciso. */
export interface EngagementCloseDto {
  /** L'incarico è eseguito (art. 12.1). */
  eseguito: boolean;
  /** Ciò che manca perché sia eseguito, in parole. */
  manca: string[];
  /** La conferma della fase 11 che chiude l'incarico (art. 12.2); null = aperto. */
  chiuso_il: string | null;
  /** La scelta sull'offerta (art. 12.3). */
  offerta: 'accettata' | 'rimandata' | 'rifiutata' | null;
  offerta_il: string | null;
}

/** Il titolare accetta, rimanda o rifiuta l'offerta del contratto n. 2 (art. 12.3). */
export const OfferChoiceSchema = z.object({ scelta: z.enum(['accettata', 'rimandata', 'rifiutata']) });
export type OfferChoiceInput = z.infer<typeof OfferChoiceSchema>;
